import { API_URLS } from "@/constants";
import apiRequest from "@/lib/axios";
import {
  AgentListFilter,
  GetList,
  Option,
  PagingResponse,
  Role,
  RoleFilter,
  Success,
  User,
  UserLoginMethods,
  UserMfaSettings,
  UserProfile,
} from "@/types";

import { RefService, makeRefService } from "./baseService";

// Roles cannot be deleted from the admin (the backend has no DELETE on
// /api-admin/role and removing a role would orphan permission grants). Strip
// the `remove` method off the generic ref service before exporting.
const fullRoleService = makeRefService<Role, RoleFilter>(
  {
    base: API_URLS.ROLE.ROLE,
    list: API_URLS.ROLE.GET_ROLES_LIST,
    options: API_URLS.ROLE.GET_ROLE_OPTIONS,
    byId: API_URLS.ROLE.ROLE_BY_ID,
  },
  "role",
);
const roleServiceWithoutRemove = Object.fromEntries(
  Object.entries(fullRoleService).filter(([key]) => key !== "remove"),
) as Omit<RefService<Role, RoleFilter>, "remove">;
export const roleService: Omit<RefService<Role, RoleFilter>, "remove"> = roleServiceWithoutRemove;

// Visitor's downline agents (players excluded) as { label: username, value: id }.
export const getUserOptions = async (): Promise<Option[]> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.GET_AGENT_OPTIONS,
    errorMessage: "Failed to load the agent options. Please try again.",
  });
};

// Payment-report cascading filter: tenant options (SuperAdmin → all tenants,
// others → only their own tenant).
export const getPaymentFilterTenants = async (): Promise<Option[]> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.PAYMENT_FILTER_TENANTS,
    errorMessage: "Failed to load the tenant options. Please try again.",
  });
};

// Agents for the payment filter, hierarchically labelled. SuperAdmin must pass a
// tenantId; others get themselves + downline.
export const getPaymentFilterAgents = async (tenantId?: string): Promise<Option[]> => {
  return apiRequest({
    method: "GET",
    url: tenantId
      ? `${API_URLS.USER.PAYMENT_FILTER_AGENTS}?tenantId=${tenantId}`
      : API_URLS.USER.PAYMENT_FILTER_AGENTS,
    errorMessage: "Failed to load the agent options. Please try again.",
  });
};

// Players hanging off the chosen agent.
export const getPaymentFilterPlayers = async (agentId: string): Promise<Option[]> => {
  return apiRequest({
    method: "GET",
    url: `${API_URLS.USER.PAYMENT_FILTER_PLAYERS}?agentId=${agentId}`,
    errorMessage: "Failed to load the player options. Please try again.",
  });
};

// Optionally scope the SuperAgent list to a tenant.
export const getSuperAgentOptions = async (tenantId?: string): Promise<Option[]> => {
  return apiRequest({
    method: "GET",
    url: tenantId
      ? `${API_URLS.USER.GET_SUPER_AGENT_OPTIONS}?tenantId=${tenantId}`
      : API_URLS.USER.GET_SUPER_AGENT_OPTIONS,
    errorMessage: "Failed to load the super agent options. Please try again.",
  });
};

export const getUser = async (id: string): Promise<User> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.USER_BY_ID(id),
    errorMessage: "Failed to load the user. Please try again.",
  });
};

export const getUserLoginMethods = async (id: string): Promise<UserLoginMethods> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.USER_LOGIN_METHODS(id),
    errorMessage: "Failed to load the sign-in methods. Please try again.",
  });
};

export const getAgentList = async (
  data: GetList<AgentListFilter>,
): Promise<PagingResponse<User>> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.USER.GET_AGENT_LIST,
    data,
    errorMessage: "Failed to load the agent list. Please try again.",
  });
};

export const getUsers = async (data: GetList<AgentListFilter>): Promise<PagingResponse<User>> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.USER.GET_USERS_LIST,
    data,
    errorMessage: "Failed to load the users. Please try again.",
  });
};

export const createUser = async (data: Partial<User>): Promise<User> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.USER.CREATE_PLAYER,
    data,
    errorMessage: "Failed to create the user. Please try again.",
  });
};

interface CreateAgentPayload {
  username: string;
  email: string;
  password: string;
  phone?: string;
  role: string;
  userType: string;
  status: string;
  currency?: string;
  timezone?: string;
  parentAgentId?: string;
  // Required only when creating a SuperAgent (it owns a brand).
  brandId?: string;
}

export const createAgent = async (data: CreateAgentPayload): Promise<User> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.USER.USER,
    data,
    errorMessage: "Failed to create the agent. Please try again.",
  });
};

// Reassign a SuperAgent's brand (Brand Setting tab). Rewires the brand <-> agent
// ownership and reprovisions the brand's casino games/providers on the backend.
export const updateUserBrand = async (userId: string, brandId: string): Promise<User> => {
  return apiRequest({
    method: "PUT",
    url: API_URLS.USER.USER_UPDATE_BRAND(userId),
    data: { brandId },
    errorMessage: "Failed to update the brand. Please try again.",
  });
};

export const changePasswordUser = async (userId: string, password: string): Promise<boolean> => {
  return apiRequest<Success>({
    method: "POST",
    url: API_URLS.USER.CHANGE_PASSWORD,
    data: { userId, password },
    errorMessage: "Failed to change the password. Please try again.",
  }).then((res) => res.success);
};

export const updateUser = async (id: string, data: Partial<User>): Promise<User> => {
  return apiRequest({
    method: "PUT",
    url: API_URLS.USER.USER_BY_ID(id),
    data,
    errorMessage: "Failed to update the user. Please try again.",
  });
};

export type BlockAction = "block" | "unblock";
export type BlockScope = "self" | "with_players" | "with_all";

export interface BlockUserResult {
  matched: number;
  modified: number;
}

export const blockUser = async (
  userId: string,
  payload: { action: BlockAction; scope: BlockScope },
): Promise<BlockUserResult> => {
  return apiRequest({
    method: "POST",
    url: API_URLS.USER.USER_BLOCK(userId),
    data: payload,
    errorMessage: "Failed to block or unblock the user. Please try again.",
  });
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.USER_PROFILE(userId),
    errorMessage: "Failed to load the user profile. Please try again.",
  });
};

export const updateUserProfile = async (
  userId: string,
  data: Partial<UserProfile>,
): Promise<UserProfile> => {
  return apiRequest({
    method: "PUT",
    url: API_URLS.USER.USER_PROFILE(userId),
    data,
    errorMessage: "Failed to update the user profile. Please try again.",
  });
};

export const getUserMfaSettings = async (userId: string): Promise<UserMfaSettings | null> => {
  return apiRequest({
    method: "GET",
    url: API_URLS.USER.USER_MFA_SETTINGS(userId),
    errorMessage: "Failed to load the user's MFA settings. Please try again.",
  });
};

export const updateUserMfaSettings = async (
  userId: string,
  data: Partial<UserMfaSettings>,
): Promise<UserMfaSettings> => {
  return apiRequest({
    method: "PUT",
    url: API_URLS.USER.USER_MFA_SETTINGS(userId),
    data,
    errorMessage: "Failed to update the user's MFA settings. Please try again.",
  });
};

export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

export const startTotpSetup = async (userId: string): Promise<TotpSetupResponse> => {
  return apiRequest({
    method: "POST",
    url: `${API_URLS.USER.USER_MFA_SETTINGS(userId)}/totp/setup`,
    errorMessage: "Failed to start the authenticator setup. Please try again.",
  });
};

export const verifyTotpSetup = async (
  userId: string,
  secret: string,
  code: string,
): Promise<UserMfaSettings> => {
  return apiRequest({
    method: "POST",
    url: `${API_URLS.USER.USER_MFA_SETTINGS(userId)}/totp/verify`,
    data: { secret, code },
    errorMessage: "Failed to verify the authenticator code. Please try again.",
  });
};

export const sendEmailOtp = async (userId: string): Promise<{ sent: boolean }> => {
  return apiRequest({
    method: "POST",
    url: `${API_URLS.USER.USER_MFA_SETTINGS(userId)}/email/send`,
    errorMessage: "Failed to send the email verification code. Please try again.",
  });
};

export const verifyEmailOtp = async (userId: string, code: string): Promise<UserMfaSettings> => {
  return apiRequest({
    method: "POST",
    url: `${API_URLS.USER.USER_MFA_SETTINGS(userId)}/email/verify`,
    data: { code },
    errorMessage: "Failed to verify the email verification code. Please try again.",
  });
};
